/**
 * Dark Mode Theme Palette
 * Matches Lawis Landing Page design — pure black luxury aesthetic
 */

import { BRAND, FONTS, DARK, STATUS } from '../design-system/tokens';

export const darkThemePalette = {
  // Page Structure
  background: {
    default: DARK.bg,               // #000000
    paper: DARK.surface,            // #0a0a0a
    navbar: DARK.bgNavbar,          // #0a0a0a
  },

  // Primary — Lawis Gold (brighter in dark mode)
  primary: {
    main: BRAND.gold,               // #c9a96e
    light: BRAND.goldLight,         // #d4b97e
    dark: BRAND.goldDark,           // #b8955a
    contrastText: '#000000',
  },

  // Secondary
  secondary: {
    main: STATUS.infoLight,         // #818cf8
    light: '#a5b4fc',
    dark: STATUS.info,              // #6366F1
    contrastText: '#ffffff',
  },

  // Status Colors (adjusted for dark mode visibility)
  success: {
    main: '#52c41a',
    light: '#6ee7b7',
    dark: STATUS.success,
  },
  warning: {
    main: '#fbbf24',
    light: '#fcd34d',
    dark: STATUS.warning,
  },
  error: {
    main: '#f472b6',
    light: '#f9a8d4',
    dark: STATUS.error,
  },
  info: {
    main: '#818cf8',
    light: '#a5b4fc',
    dark: STATUS.info,
  },

  // Text Colors
  text: {
    primary: DARK.textPrimary,      // #f5f5f5
    secondary: DARK.textSecondary,  // #a8a8a8
    disabled: DARK.textTertiary,    // #737373
    tertiary: DARK.textTertiary,
  },

  // Dividers and Borders
  divider: DARK.borderPrimary,      // #2d2d2d
  border: DARK.borderSecondary,     // #1a1a1a

  // Neutral Grey Scale (inverted)
  grey: {
    50: '#0a0a0a',
    100: '#111111',
    200: '#1a1a1a',
    300: '#2d2d2d',
    400: '#4a4a4a',
    500: '#737373',
    600: '#a8a8a8',
    700: '#d4d4d4',
    800: '#e5e5e5',
    900: '#f5f5f5',
  },

  // Action States
  action: {
    active: BRAND.gold,
    hover: DARK.surfaceHover,
    selected: 'rgba(201, 169, 110, 0.08)',
    disabled: DARK.textTertiary,
    disabledBackground: DARK.bgTertiary,
  },

  // Brand Colors
  brand: {
    gold: BRAND.gold,
    goldSubtle: BRAND.goldSubtle,
    goldBorder: BRAND.goldBorder,
  },
};

/**
 * Dark Mode Typography Configuration
 */
export const darkTypography = {
  fontFamily: FONTS.body,

  h1: {
    fontFamily: FONTS.display,
    fontSize: '28px',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: FONTS.display,
    fontSize: '22px',
    fontWeight: 400,
    lineHeight: 1.25,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontFamily: FONTS.display,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: FONTS.body,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  h5: {
    fontFamily: FONTS.body,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontFamily: FONTS.body,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontFamily: FONTS.body,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontFamily: FONTS.body,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontFamily: FONTS.body,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.03em',
  },
  subtitle1: {
    fontFamily: FONTS.body,
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontFamily: FONTS.body,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
};

/**
 * Dark Mode Component Overrides
 */
export const darkComponentOverrides = {
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        border: `1px solid ${DARK.borderPrimary}`,
        backgroundColor: DARK.surface,
        boxShadow: DARK.shadowCard,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: '#3a3a3a',
          boxShadow: DARK.shadowElevated,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        borderColor: DARK.borderPrimary,
      },
      elevation0: {
        boxShadow: 'none',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        border: `1px solid ${DARK.borderPrimary}`,
        borderRadius: '12px',
        boxShadow: DARK.shadowElevated,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 500,
        fontFamily: FONTS.body,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      containedPrimary: {
        backgroundColor: BRAND.gold,
        color: '#000000',
        '&:hover': {
          backgroundColor: BRAND.goldLight,
        },
      },
      containedSecondary: {
        backgroundColor: STATUS.infoLight,
        color: '#000000',
        '&:hover': {
          backgroundColor: '#a5b4fc',
        },
      },
      outlined: {
        borderColor: DARK.borderPrimary,
        color: DARK.textPrimary,
        '&:hover': {
          backgroundColor: DARK.surfaceHover,
          borderColor: '#3a3a3a',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          color: DARK.textPrimary,
          '& fieldset': {
            borderColor: DARK.borderPrimary,
          },
          '&:hover fieldset': {
            borderColor: '#3a3a3a',
          },
          '&.Mui-focused fieldset': {
            borderColor: BRAND.gold,
          },
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&.Mui-selected': {
          backgroundColor: BRAND.goldSubtle,
          '&:hover': {
            backgroundColor: 'rgba(201, 169, 110, 0.16)',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontFamily: FONTS.body,
        fontWeight: 500,
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        border: `1px solid ${DARK.borderPrimary}`,
        boxShadow: DARK.shadowElevated,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        borderColor: DARK.borderPrimary,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        border: `1px solid ${DARK.borderPrimary}`,
        boxShadow: DARK.shadowElevated,
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        backgroundColor: DARK.surface,
        backgroundImage: 'none',
        border: `1px solid ${BRAND.goldBorder}`,
        borderRadius: '12px',
        boxShadow: DARK.shadowElevated,
        color: DARK.textPrimary,
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        color: DARK.textPrimary,
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        color: DARK.textPrimary,
      },
      label: {
        color: DARK.textPrimary,
      },
      switchViewButton: {
        color: DARK.textSecondary,
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        color: DARK.textSecondary,
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: {
        color: DARK.textTertiary,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: DARK.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.15)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#000000',
          '&:hover': {
            backgroundColor: BRAND.goldDark,
          },
          '&:focus': {
            backgroundColor: BRAND.gold,
          },
        },
        '&.MuiPickersDay-today': {
          borderColor: BRAND.gold,
        },
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      yearButton: {
        color: DARK.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.15)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#000000',
        },
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        color: DARK.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.15)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#000000',
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#2d2d2d',
        color: DARK.textPrimary,
        border: `1px solid #4a4a4a`,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.8)',
        fontSize: '12px',
        fontFamily: FONTS.body,
        borderRadius: '8px',
        padding: '8px 12px',
        maxWidth: 320,
      },
      arrow: {
        color: '#2d2d2d',
        '&::before': {
          border: `1px solid #4a4a4a`,
        },
      },
    },
  },
};
